import { useCallback, useEffect, useRef, useState } from 'react'
import { useRoomContext } from '@livekit/components-react'
import {
  ConnectionQuality,
  RoomEvent,
  Track,
  VideoQuality,
  type RemoteParticipant,
  type RemoteTrackPublication,
} from 'livekit-client'

import {
  INITIAL_AUTO_STATE,
  OVERRIDE_CLEAR_MS,
  rebaseClocks,
  shareVideoQuality,
  SMALL_CLASS_MAX_CAMERAS,
  stepAuto,
  TICK_MS,
  wantsCamera,
  type AutoState,
  type QualitySignal,
  type SaverLevel,
} from './dataSaver'
import { report } from './diagnostics'
import { readLinkHealth, type LinkHealth } from './MediaReporter'
import { isTeacher } from './useClassroomSignals'

/**
 * Data saver: the honest low-bandwidth mode.
 *
 * Two ways in:
 * - MANUAL — the child (or tutor) taps the control; goes straight to L2 and
 *   stays until they tap it off. Remembered per device: a phone that needed it
 *   once almost certainly needs it next lesson.
 * - AUTO — the local connection quality LiveKit reports runs the hysteresis
 *   machine in dataSaver.ts. Reluctant to degrade, forgiving in recovery, and
 *   it never fights a manual choice.
 *
 * What it does: unsubscribes remote CAMERA tracks per level; never audio.
 * The screen share is never unsubscribed — at L2 its subscribed simulcast
 * layer is capped to the mid rung instead (dataSaver.shareVideoQuality),
 * which stays compatible with the share's always-visible pin.
 * Unsubscribing cameras is also what lets dynacast stop the
 * publisher encoding layers nobody watches — the weak phone stops paying
 * uplink for video nobody sees.
 *
 * Honesty is a feature: the level is broadcast via a participant attribute so
 * the teacher's roster/tiles can say "weak connection, they can still hear
 * you" instead of the class reading a blank tile as a child ignoring them.
 *
 * And the user outranks the machine: the auto toast carries "Resume videos",
 * which overrules the ladder until the link has been good for a sustained run.
 * The first cohort's tutors experienced an over-eager ladder as the product
 * breaking, and had no recourse but a refresh — an escape hatch is cheaper
 * than their trust.
 */

const SAVER_ATTRIBUTE = 'data_saver'
const MANUAL_STORE_KEY = 'ischool-meet:data-saver'
const DOWNLINK_LOSS_RATIO = 0.05
const DROPPED_FRAME_RATIO = 0.05

type Credential = { code?: string | null; reconnectToken?: string | null }

export interface DataSaverApi {
  /** Effective level right now (manual forces 2). Rules wantsCamera. */
  level: SaverLevel
  /**
   * The level as the USER experiences it: a small-class Host at L1 keeps
   * every camera, so there is nothing to explain and nothing to light up.
   * Toasts and the control bar read this; the camera policy reads `level`.
   */
  noticeLevel: SaverLevel
  /** Small-class Host exemption active (Stage needs it for tile labels). */
  keepStudents: boolean
  manual: boolean
  /** True when a level the user can SEE came from the ladder, not from them. */
  autoEngaged: boolean
  toggleManual: () => void
  /** "Resume videos": back to L0, ladder overruled for this bad episode. */
  resumeVideos: () => void
  /** Remote identities currently in saver mode or reporting poor quality. */
  weakPeers: Record<string, 'saver' | 'poor'>
  /** The explanation toast: dismissible, single source of truth lives here. */
  noteDismissed: boolean
  dismissNote: () => void
  /** Re-open the explanation (the lit control taps here during auto). */
  showNote: () => void
}

function loadManual(): boolean {
  try {
    return localStorage.getItem(MANUAL_STORE_KEY) === '1'
  } catch {
    return false
  }
}

function saveManual(on: boolean): void {
  try {
    if (on) localStorage.setItem(MANUAL_STORE_KEY, '1')
    else localStorage.removeItem(MANUAL_STORE_KEY)
  } catch {
    /* private mode — the choice just does not persist */
  }
}

function toSignal(quality: ConnectionQuality): QualitySignal {
  switch (quality) {
    case ConnectionQuality.Poor:
    case ConnectionQuality.Lost:
      return 'poor'
    case ConnectionQuality.Excellent:
    case ConnectionQuality.Good:
      return 'ok'
    default:
      return 'unknown'
  }
}

/**
 * LiveKit's qualitative quality estimate is intentionally pessimistic. Only
 * its *escalation* is gated here: recovery and the unknown-freeze machine stay
 * exactly as defined in dataSaver.ts. Downlink gets priority because pausing
 * classmates' video only helps a client that is struggling to receive it.
 */
function corroboratesPoorLink(current: LinkHealth, previous: LinkHealth | null): boolean {
  const receivedDelta = current.inboundVideoPacketsReceived - (previous?.inboundVideoPacketsReceived ?? current.inboundVideoPacketsReceived)
  const lostDelta = current.inboundVideoPacketsLost - (previous?.inboundVideoPacketsLost ?? current.inboundVideoPacketsLost)
  const totalPackets = receivedDelta + lostDelta
  const downlinkLoss = totalPackets > 0 && lostDelta / totalPackets >= DOWNLINK_LOSS_RATIO
  const droppedFrames = current.inboundVideoFramesDropped - (previous?.inboundVideoFramesDropped ?? current.inboundVideoFramesDropped)
  const decodedFrames = current.inboundVideoFramesDecoded - (previous?.inboundVideoFramesDecoded ?? current.inboundVideoFramesDecoded)
  const totalFrames = decodedFrames + droppedFrames
  const downlinkFrameDrops = totalFrames > 0 && droppedFrames / totalFrames >= DROPPED_FRAME_RATIO
  return downlinkLoss || downlinkFrameDrops
}

export function useDataSaver(isHost: boolean, credential: Credential): DataSaverApi {
  const room = useRoomContext()
  const [manual, setManual] = useState(loadManual)
  // The WHOLE machine state lives in React state: `poorSince` becoming non-null
  // must re-render so the tick interval below arms — with only the level in
  // state, a steady Poor (LiveKit fires quality events on CHANGE only) would
  // set the clock once and never escalate.
  const [autoState, setAutoState] = useState<AutoState>(INITIAL_AUTO_STATE)
  // A Poor label arms sampling even while evidence has not yet accumulated;
  // otherwise an honest initial `unknown` would never receive the next sample.
  const [qualitySignal, setQualitySignal] = useState<QualitySignal>('unknown')
  const [weakPeers, setWeakPeers] = useState<Record<string, 'saver' | 'poor'>>({})
  const [noteDismissed, setNoteDismissed] = useState(false)
  // True while "Resume videos" holds the ladder off. In state because the tick
  // interval must stay armed to notice the link recovering. NOT a countdown —
  // see OVERRIDE_CLEAR_MS.
  const [override, setOverride] = useState(false)

  const autoRef = useRef<AutoState>(autoState)
  const qualityRef = useRef<QualitySignal>('unknown')
  const manualRef = useRef(manual)
  manualRef.current = manual
  const overrideRef = useRef(override)
  overrideRef.current = override
  /** Start of the good run that will retire the override, or null. */
  const overrideOkSinceRef = useRef<number | null>(null)
  const credentialRef = useRef(credential)
  credentialRef.current = credential
  const linkHealthRef = useRef<LinkHealth | null>(null)
  const poorEvidenceRef = useRef(false)
  const advancingRef = useRef(false)

  // Small-class Host exemption. A ref for synchronous reads inside event
  // handlers (the policy must be right for the publication being applied NOW),
  // state so Stage re-renders tile labels when it flips.
  const keepStudentsRef = useRef(false)
  const [keepStudents, setKeepStudents] = useState(false)

  const level: SaverLevel = manual ? 2 : autoState.level
  const levelRef = useRef(level)
  levelRef.current = level

  // What the user can SEE: a small-class Host at L1 lost nothing — no toast,
  // no lit control, and nothing to tell the room about. The camera policy
  // still runs on `level` (L2 remains reachable).
  const keepStudentsHidesLevel = level === 1 && keepStudents
  const noticeLevel: SaverLevel = keepStudentsHidesLevel ? 0 : level

  const refreshKeepStudents = useCallback(() => {
    if (!room) return
    let cameras = 0
    room.remoteParticipants.forEach((participant: RemoteParticipant) => {
      // isCameraEnabled, NOT "has a camera publication": turning a camera off
      // MUTES the track and leaves the publication in place (only a screen
      // share is unpublished on disable), so counting publications counted
      // dark cameras — a 1:1 where four people had once had cameras on would
      // lose the exemption that exists for exactly that lesson.
      if (participant.isCameraEnabled) cameras += 1
    })
    const next = isHost && cameras <= SMALL_CLASS_MAX_CAMERAS
    if (keepStudentsRef.current !== next) {
      keepStudentsRef.current = next
      setKeepStudents(next)
    }
  }, [room, isHost])

  // ---- applying the policy --------------------------------------------------
  const applyToPublication = useCallback((pub: RemoteTrackPublication, identity: string) => {
    if (pub.kind !== Track.Kind.Video) return
    // The share is the lesson — never unsubscribed by the saver, but L2 caps
    // its simulcast layer (LK-027). setVideoQuality only lowers the maximum
    // layer the SFU may serve on the LIVE subscription; it can never
    // unsubscribe or pause the share, so the always-visible pin in
    // useScreenSharePin keeps working on the lower rung.
    if (pub.source === Track.Source.ScreenShare) {
      pub.setVideoQuality(
        shareVideoQuality(levelRef.current) === 'medium' ? VideoQuality.MEDIUM : VideoQuality.HIGH,
      )
      return
    }
    const wanted = wantsCamera(levelRef.current, isTeacher(identity), keepStudentsRef.current)
    if (!wanted) {
      // Record the desire on isDesired, not isSubscribed: a JUST-published
      // camera is not subscribed YET, but autoSubscribe is about to subscribe
      // it server-side — gating on isSubscribed skipped the unsubscribe here
      // and the camera slipped through the saver moments later.
      if (pub.isDesired || pub.isSubscribed) pub.setSubscribed(false)
    } else if (!pub.isSubscribed) {
      pub.setSubscribed(true)
    }
  }, [])

  const applyAll = useCallback(() => {
    if (!room) return
    room.remoteParticipants.forEach((participant: RemoteParticipant) => {
      participant.videoTrackPublications.forEach((pub) => {
        applyToPublication(pub as RemoteTrackPublication, participant.identity)
      })
    })
  }, [room, applyToPublication])

  // Re-apply whenever the effective policy changes, and tell the room about it.
  // `keepStudents` is a policy input: when the fourth camera arrives (or the
  // class shrinks back), every already-applied decision must be revisited.
  useEffect(() => {
    applyAll()
    if (!room) return
    // Advertised from noticeLevel, not `level`: this attribute is what puts a
    // "weak connection" badge on us in everyone else's roster, and a
    // small-class Host at L1 has dropped NOTHING — claiming saver mode there
    // would tell the class the tutor is struggling when they are not.
    const value = noticeLevel === 0 ? '' : manual ? 'manual' : 'auto'
    room.localParticipant
      .setAttributes({ [SAVER_ATTRIBUTE]: value })
      .catch(() => undefined /* attribute is advisory; the mode still works */)
  }, [level, noticeLevel, keepStudents, manual, room, applyAll])

  // ---- the auto ladder ---------------------------------------------------------
  const advance = useCallback(async () => {
    if (advancingRef.current || manualRef.current) return // manual owns the level; the machine sleeps
    advancingRef.current = true
    try {
      const now = Date.now()
      if (overrideRef.current) {
        // The tutor overruled the ladder. It stays overruled for as long as the
        // bad patch lasts — only the link proving itself GOOD for a sustained
        // run hands judgment back, and only then can a genuinely NEW episode
        // degrade anything. A timer here would have blinked the videos off again
        // mid-lesson, which is the surprise the override exists to prevent.
        if (qualityRef.current === 'ok') {
          const okSince = overrideOkSinceRef.current ?? now
          overrideOkSinceRef.current = okSince
          if (now - okSince >= OVERRIDE_CLEAR_MS) {
            overrideOkSinceRef.current = null
            overrideRef.current = false
            setOverride(false)
            autoRef.current = rebaseClocks(INITIAL_AUTO_STATE, 'ok', now)
            setAutoState(autoRef.current)
          }
        } else if (qualityRef.current === 'poor') {
          overrideOkSinceRef.current = null // same episode, still overruled
        }
        return
      }

      let signal = qualityRef.current
      if (signal === 'poor') {
        // The first sample establishes a baseline. Subsequent five-second samples
        // measure loss/drops/bitrate rather than trusting a transient SDK label.
        const health = await readLinkHealth(room)
        const previousHealth = linkHealthRef.current
        if (health) linkHealthRef.current = health
        const corroborated = health !== undefined && corroboratesPoorLink(health, previousHealth)
        if (qualityRef.current === 'poor' && corroborated) {
          // A provisional Poor label does not get to donate clock time. Start a
          // fresh poor run when the browser first proves the link is impaired.
          if (!poorEvidenceRef.current) {
            poorEvidenceRef.current = true
            autoRef.current = rebaseClocks(autoRef.current, 'poor', now)
            setAutoState(autoRef.current)
          }
        } else {
          poorEvidenceRef.current = false
          signal = qualityRef.current === 'poor' ? 'unknown' : qualityRef.current
        }
      }

      const next = stepAuto(autoRef.current, signal, now)
      if (next !== autoRef.current) {
        if (next.level !== autoRef.current.level) {
          // Field telemetry: the first cohort's "camera turned off on a stable
          // connection" reports were unverifiable — now every auto transition
          // is counted server-side.
          report(credentialRef.current, {
            stage: 'permissions',
            outcome: 'ok',
            permission: `saver=auto-l${next.level}`,
          })
        }
        autoRef.current = next
        setAutoState(next)
      }
    } finally {
      advancingRef.current = false
    }
  }, [room])

  // ---- room events ------------------------------------------------------------
  useEffect(() => {
    if (!room) return

    // Seed from the CURRENT quality: LiveKit only emits on change, so a client
    // that joins (or re-mounts) already-Poor would otherwise wait for a flap
    // before the machine ever saw it.
    const seededQuality = toSignal(room.localParticipant.connectionQuality)
    qualityRef.current = seededQuality
    setQualitySignal(seededQuality)
    autoRef.current = rebaseClocks(autoRef.current, seededQuality, Date.now())
    setAutoState(autoRef.current)
    poorEvidenceRef.current = false
    refreshKeepStudents()
    void advance()

    // New publication while degraded: unsubscribe it before it costs anything.
    // Refresh the exemption FIRST — the publication being applied might be the
    // fourth camera that flips it off (the applyAll effect then revisits the
    // other three).
    const onPublished = (pub: RemoteTrackPublication, participant: RemoteParticipant) => {
      refreshKeepStudents()
      applyToPublication(pub, participant.identity)
    }
    const onUnpublished = () => refreshKeepStudents()
    // A camera going dark or coming back changes the count the exemption is
    // computed from — and mute is how cameras actually go off.
    const onMuteChanged = () => refreshKeepStudents()
    // A participant who was already publishing when we joined.
    const onConnected = (participant: RemoteParticipant) => {
      refreshKeepStudents()
      participant.videoTrackPublications.forEach((pub) =>
        applyToPublication(pub as RemoteTrackPublication, participant.identity),
      )
    }
    // A full reconnect can hand back server-default subscriptions.
    const onReconnected = () => {
      refreshKeepStudents()
      applyAll()
    }

    const onQuality = (quality: ConnectionQuality, participant: { isLocal: boolean; identity: string }) => {
      if (participant.isLocal) {
        const previous = qualityRef.current
        qualityRef.current = toSignal(quality)
        setQualitySignal(qualityRef.current)
        if (qualityRef.current !== 'poor') {
          linkHealthRef.current = null
          poorEvidenceRef.current = false
        }
        // Leaving an `unknown` stretch (reconnects) restarts the active clock:
        // wall time kept moving while we had no evidence, and that gap must
        // not count toward escalation or recovery.
        if (previous === 'unknown' && qualityRef.current !== 'unknown') {
          autoRef.current = rebaseClocks(autoRef.current, qualityRef.current, Date.now())
        }
        void advance()
        return
      }
      // Remote quality feeds the teacher-facing honesty map.
      setWeakPeers((current) => {
        const poor = quality === ConnectionQuality.Poor || quality === ConnectionQuality.Lost
        const existing = current[participant.identity]
        if (existing === 'saver') return current // explicit saver wins the label
        if (poor && existing !== 'poor') return { ...current, [participant.identity]: 'poor' }
        if (!poor && existing === 'poor') {
          const next = { ...current }
          delete next[participant.identity]
          return next
        }
        return current
      })
    }

    const readSaverAttributes = () => {
      setWeakPeers((current) => {
        const next: Record<string, 'saver' | 'poor'> = {}
        // Keep 'poor' entries for participants still present; overlay saver flags.
        room.remoteParticipants.forEach((p: RemoteParticipant) => {
          if (p.attributes?.[SAVER_ATTRIBUTE]) next[p.identity] = 'saver'
          else if (current[p.identity] === 'poor') next[p.identity] = 'poor'
        })
        // Cheap shallow-equality guard: identical keys AND values → keep the
        // old object so subscribers do not re-render.
        const currentKeys = Object.keys(current)
        const nextKeys = Object.keys(next)
        if (
          currentKeys.length === nextKeys.length &&
          nextKeys.every((key) => current[key] === next[key])
        ) {
          return current
        }
        return next
      })
    }

    const onDisconnectedParticipant = () => {
      refreshKeepStudents()
      readSaverAttributes()
    }

    room.on(RoomEvent.TrackPublished, onPublished)
    room.on(RoomEvent.TrackUnpublished, onUnpublished)
    room.on(RoomEvent.TrackMuted, onMuteChanged)
    room.on(RoomEvent.TrackUnmuted, onMuteChanged)
    room.on(RoomEvent.ParticipantConnected, onConnected)
    room.on(RoomEvent.Reconnected, onReconnected)
    room.on(RoomEvent.ConnectionQualityChanged, onQuality)
    room.on(RoomEvent.ParticipantAttributesChanged, readSaverAttributes)
    room.on(RoomEvent.ParticipantDisconnected, onDisconnectedParticipant)
    readSaverAttributes()
    return () => {
      room.off(RoomEvent.TrackPublished, onPublished)
      room.off(RoomEvent.TrackUnpublished, onUnpublished)
      room.off(RoomEvent.TrackMuted, onMuteChanged)
      room.off(RoomEvent.TrackUnmuted, onMuteChanged)
      room.off(RoomEvent.ParticipantConnected, onConnected)
      room.off(RoomEvent.Reconnected, onReconnected)
      room.off(RoomEvent.ConnectionQualityChanged, onQuality)
      room.off(RoomEvent.ParticipantAttributesChanged, readSaverAttributes)
      room.off(RoomEvent.ParticipantDisconnected, onDisconnectedParticipant)
    }
  }, [room, applyAll, applyToPublication, advance, refreshKeepStudents])

  // Quality events are sparse; a slow tick moves the clocks between them.
  // Armed while there is something to move — a poor run counting toward
  // escalation, a good run counting toward recovery, a resume hold waiting to
  // expire, or a Poor label collecting the evidence needed to trust it. A
  // healthy L0 class runs no timer.
  const ticking =
    !manual && (autoState.level > 0 || autoState.poorSince !== null || override || qualitySignal === 'poor')
  useEffect(() => {
    if (!ticking) return
    const id = window.setInterval(() => void advance(), TICK_MS)
    return () => window.clearInterval(id)
  }, [ticking, advance])

  const toggleManual = useCallback(() => {
    // Computed OUTSIDE the setState updater: updaters must stay pure
    // (StrictMode runs them twice), and this branch has side effects.
    const next = !manualRef.current
    saveManual(next)
    report(credentialRef.current, {
      stage: 'permissions',
      outcome: 'ok',
      permission: next ? 'saver=manual-on' : 'saver=manual-off',
    })
    // Either edge of the manual switch RETIRES the override: a manual cycle is
    // the user taking the level under their own control, and a live override
    // would otherwise keep the ladder asleep straight through the manual-off
    // re-arm below, whose whole purpose is to react to a still-poor link.
    overrideRef.current = false
    setOverride(false)
    overrideOkSinceRef.current = null
    if (!next) {
      // Coming out of manual: restart from clean clocks rather than instantly
      // re-degrading off a stale poor run…
      autoRef.current = INITIAL_AUTO_STATE
      setAutoState(INITIAL_AUTO_STATE)
      // …but RE-ARM immediately. Quality events fire on CHANGE only; if the
      // link is still poor (usually why the toggle was tried), no event ever
      // comes and the ladder would sleep forever on a bad link. The microtask
      // runs after manualRef flips below.
      queueMicrotask(() => void advance())
    }
    setManual(next)
    manualRef.current = next
    setNoteDismissed(false)
  }, [advance])

  const resumeVideos = useCallback(() => {
    // The user outranks the machine. Back to L0 now (the applyAll effect
    // resubscribes) and the ladder is overruled for the rest of this bad
    // episode; it only gets a vote again once the link has been good for a
    // sustained run (see advance()).
    overrideRef.current = true
    setOverride(true)
    overrideOkSinceRef.current = null
    autoRef.current = INITIAL_AUTO_STATE
    setAutoState(INITIAL_AUTO_STATE)
    report(credentialRef.current, {
      stage: 'permissions',
      outcome: 'ok',
      permission: 'saver=resume-tap',
    })
  }, [])

  const dismissNote = useCallback(() => setNoteDismissed(true), [])
  const showNote = useCallback(() => setNoteDismissed(false), [])

  // A visible level CHANGE is news — it un-dismisses the explanation.
  useEffect(() => {
    setNoteDismissed(false)
  }, [noticeLevel])

  return {
    level,
    noticeLevel,
    keepStudents,
    manual,
    autoEngaged: !manual && noticeLevel > 0,
    toggleManual,
    resumeVideos,
    weakPeers,
    noteDismissed,
    dismissNote,
    showNote,
  }
}
